import { useState, useEffect, useRef } from 'react';
import CreativeExplore from '../../../services/creativeExplore';
import { getCreativesDefaultParams } from '../../creativesQueryUtils';
import { BREAKDOWN_DIMENSIONS } from '../../consts';

const CreativeExploreAPI = new CreativeExplore();

const flattenCohortData = (row, cohortMetrics, cohortPeriods) => {
    const flattenedRow = { ...row };
    cohortMetrics.forEach(metric => {
        cohortPeriods.forEach(period => {
            const flatKey = `${metric}_${period}`;
            if (row[metric] && row[metric][period] !== undefined) {
                flattenedRow[flatKey] = row[metric][period];
            }
        });
    });
    return flattenedRow;
};

export const useCreativePerformanceData = ({
    app,
    source,
    imageHash,
    dimensions = [],
    metrics = [],
    cohortFields = [],
    dateRange,
    timeBreakdown = 'day',
    shouldSkip = false,
}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const dimensionsKey = JSON.stringify(dimensions);
    const metricsKey = JSON.stringify(metrics);
    const cohortFieldsKey = JSON.stringify(cohortFields);

    useEffect(() => {
        if (shouldSkip) {
            setLoading(false);
            return;
        }

        if (!app || !source || !imageHash || !metrics.length || !dateRange) {
            setData([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setData([]);

        const baseParams = getCreativesDefaultParams(
            dateRange,
            app,
            source,
            metrics,
            cohortFields,
            null
        );

        const queryParams = {
            ...baseParams,
            dimensions: [...dimensions, 'creative_image_hash'],
            time_breakdown: [timeBreakdown],
            filters: [
                { dimension: 'app', operator: 'in', values: [app], options: { no_lowercase: true } },
                { dimension: 'source', operator: 'in', values: [source], options: { no_lowercase: true } },
            ],
            post_process_filters: [
                {
                    field: 'creative_image_hash',
                    operator: 'equals',
                    value: imageHash,
                    valueTo: null,
                },
            ],
        };

        CreativeExploreAPI.runQuery(queryParams, { unique: true, suffix: dimensionsKey })
            .then(response => {
                if (cancelled) return;
                const results = (response.results || []).map(row =>
                    flattenCohortData(row, baseParams.cohort_metrics, baseParams.cohort_periods)
                );
                setData(results);
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Error fetching creative performance data:', err);
                setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        shouldSkip,
        app,
        source,
        imageHash,
        dimensionsKey,
        metricsKey,
        cohortFieldsKey,
        timeBreakdown,
        dateRange.startDate,
        dateRange.endDate,
    ]);

    return { loading, data, error };
};

/**
  Checks if the fetched data belongs to the requested breakdown.
  This validation is necessary because React effects run after render. When switching breakdowns,
  there's a render cycle where `selectedBreakdown` has changed but `data` still contains
  results from the previous breakdown (the effect hasn't run yet to fetch new data).
 */
const dataMatchesBreakdown = (data, breakdownValue) => {
    if (!data?.length) return false;
    return breakdownValue in data[0];
};

export const useBreakdownChartData = ({ app, source, imageHash, metrics, cohortFields, dateRange }) => {
    const [selectedBreakdown, setSelectedBreakdown] = useState(BREAKDOWN_DIMENSIONS.COUNTRY);
    const cacheRef = useRef({});

    const cachedData = cacheRef.current[selectedBreakdown.value];

    const { loading, data, error } = useCreativePerformanceData({
        app,
        source,
        imageHash,
        dimensions: [selectedBreakdown.value],
        metrics,
        cohortFields,
        dateRange,
        timeBreakdown: 'all',
        shouldSkip: !!cachedData,
    });

    const hasValidData = !loading && dataMatchesBreakdown(data, selectedBreakdown.value) && !error;

    useEffect(() => {
        if (hasValidData && !cachedData) {
            cacheRef.current[selectedBreakdown.value] = data;
        }
    }, [data, hasValidData, selectedBreakdown.value]);

    const isLoading = loading || (!cachedData && !hasValidData && !error);
    const chartData = cachedData || (hasValidData ? data : []);

    return {
        data: chartData,
        loading: isLoading,
        selectedBreakdown,
        setSelectedBreakdown,
    };
}
